import React, { useState, useEffect } from 'react';
import { resolveImage, landingAPI } from '../../utils/api';

// Helper to resolve bilingual field {en, zh} or plain string
const tField = (field, lang = 'en') => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field[lang] || field.en || '';
};

const formatSize = (sz, lang = 'en') => {
  if (!sz) return '';
  if (typeof sz === 'object') {
    return sz[lang] || sz.en || sz.zh || Object.values(sz)[0] || '';
  }
  return String(sz);
};

const FALLBACK_PRODUCTS = [
  {
    _id: 'prod_1',
    name: { en: 'Professional Split-Sole Ballet Shoes', zh: '專業真皮軟底芭蕾舞鞋' },
    description: { en: 'High-grade canvas & soft leather arch support for maximum flexibility and grace.', zh: '優質帆布配合柔軟真皮足弓支撐，展現極致靈活性與優雅。' },
    price: 180,
    gender: 'Unisex',
    sizes: ['26', '28', '30', '32', '34', '36', '38'],
    inStock: true,
    imageUrl: 'https://images.unsplash.com/photo-1547153760-18fc86324498?auto=format&fit=crop&w=600&q=80'
  },
  {
    _id: 'prod_2',
    name: { en: 'ETS Signature Dance Leotard', zh: 'ETS 經典芭蕾練功服' },
    description: { en: 'Breathable, moisture-wicking four-way stretch fabric with elegant scoop neckline.', zh: '透氣排汗四面彈力面料，配以優雅大圓領設計，活動自如。' },
    price: 260,
    gender: 'Female',
    sizes: ['XS', 'S', 'M', 'L', 'XL'],
    inStock: true,
    imageUrl: 'https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?auto=format&fit=crop&w=600&q=80'
  },
  {
    _id: 'prod_3',
    name: { en: 'High-Elasticity Convertible Dance Tights', zh: '高彈力兩用專業舞蹈褲襪' },
    description: { en: 'Ultra-soft microfiber tights with convertible foot opening for rapid shoe changes.', zh: '超柔超細纖維，腳底開口設計，方便隨時轉換鞋履練習。' },
    price: 95,
    gender: 'Female',
    sizes: ['Kids S', 'Kids L', 'Adult S', 'Adult M', 'Adult L'],
    inStock: true,
    imageUrl: 'https://images.unsplash.com/photo-1518834107812-67b0b7c58434?auto=format&fit=crop&w=600&q=80'
  },
  {
    _id: 'prod_4',
    name: { en: 'ETS Academy Dance Gear Duffel Bag', zh: 'ETS 學院專業舞蹈裝備手提包' },
    description: { en: 'Spacious water-resistant duffel with dedicated ventilated shoe compartment.', zh: '大容量防潑水手提旅行袋，特設獨立透氣鞋隔，實用耐用。' },
    price: 320,
    gender: 'Unisex',
    sizes: ['Standard'],
    inStock: true,
    imageUrl: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?auto=format&fit=crop&w=600&q=80'
  },
  {
    _id: 'prod_5',
    name: { en: 'Urban Streetwear Oversized Dance Tee', zh: '街頭潮流寬鬆印花練舞T恤' },
    description: { en: '100% heavyweight combed cotton for relaxed street dance and hip hop practice.', zh: '100% 精梳重磅純棉，寬鬆舒適剪裁，專為街舞及日常練習打造。' },
    price: 220,
    gender: 'Unisex',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    inStock: true,
    imageUrl: 'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?auto=format&fit=crop&w=600&q=80'
  }
];

export default function MerchandiseSection({ publicMerchandise = [], whatsappNumber, lang = 'en' }) {
  const displayItems = (publicMerchandise && publicMerchandise.length > 0)
    ? publicMerchandise
    : FALLBACK_PRODUCTS;

  const [slideIdx, setSlideIdx] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);
  const [activeWhatsApp, setActiveWhatsApp] = useState(whatsappNumber || '+852 5135 3966');

  // Load support WhatsApp from public admin settings if available
  useEffect(() => {
    if (whatsappNumber) {
      setActiveWhatsApp(whatsappNumber);
      return;
    }
    landingAPI.getPublicSettings().then(res => {
      if (res.data?.supportWhatsApp) {
        setActiveWhatsApp(res.data.supportWhatsApp);
      }
    }).catch(() => {});
  }, [whatsappNumber]);

  // Responsive columns calculator matching Classes.jsx
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) setVisibleCards(1);
      else if (width < 1024) setVisibleCards(2);
      else setVisibleCards(3);
    };

    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIdx = Math.max(0, displayItems.length - visibleCards);

  useEffect(() => {
    if (slideIdx > maxIdx) {
      setSlideIdx(maxIdx);
    }
  }, [maxIdx, slideIdx]);

  const handleWhatsAppInquiry = (item) => {
    const name = tField(item.name, 'en') || 'ETS Product';
    const cleanNum = (activeWhatsApp || '').replace(/\D/g, '') || '85251353966';
    const message = encodeURIComponent(
      `Hello ETS Academy, I am interested in ordering the school product: "${name}" (HK$ ${item.price}). Please share size availability and purchase details.`
    );
    window.open(`https://wa.me/${cleanNum}?text=${message}`, '_blank');
  };

  return (
    <section className="lp-sec lp-sec-alt" id="merchandise" style={{ background: '#fdfbfb' }}>
      <div className="lp-container">
        {/* Section Header */}
        <div
          className="reveal"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: 20,
            marginBottom: 52,
          }}
        >
          <div>
            <div className="lp-overline" style={{ color: '#7b1d3a', letterSpacing: '2.5px' }}>
              {lang === 'zh' ? '學院專屬裝備' : 'Official Dancewear & Gear'}
            </div>
            <h2 className="lp-sec-title" style={{ marginBottom: 0 }}>
              {lang === 'zh' ? '舞蹈裝備及' : 'Academy '}
              <em className="lp-text-gold">{lang === 'zh' ? '學校周邊產品' : 'Merchandise'}</em>
            </h2>
          </div>
          <p className="lp-sec-sub" style={{ maxWidth: 420, margin: 0 }}>
            {lang === 'zh'
              ? '備有專業芭蕾舞鞋、學院制服、練功服及必備舞蹈配件，提供即時諮詢與訂購服務。'
              : 'Explore official ETS Academy uniforms, performance shoes, and essential dancewear for all levels.'}
          </p>
        </div>

        {/* Carousel Viewport */}
        <div style={{ overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              gap: 24,
              transition: 'transform .4s ease',
              transform: `translateX(calc(-${slideIdx} * (100% / ${visibleCards} + 24px / ${visibleCards})))`,
            }}
          >
            {displayItems.map((item) => (
              <div
                key={item._id}
                className="lp-class-card"
                style={{
                  flex: `0 0 calc(${100 / visibleCards}% - ${(24 * (visibleCards - 1)) / visibleCards}px)`,
                  minWidth: 280,
                  borderRadius: 8,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                  border: '1px solid #ebebeb',
                }}
              >
                {/* Product Image */}
                <div style={{ overflow: 'hidden', height: 230, position: 'relative', background: '#f5f5f5' }}>
                  <img
                    src={resolveImage(item.imageUrl) || FALLBACK_PRODUCTS[0].imageUrl}
                    alt={tField(item.name, lang)}
                    className="lp-class-img"
                    style={{ height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = FALLBACK_PRODUCTS[0].imageUrl;
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      background: '#7b1d3a',
                      color: '#ffffff',
                      padding: '4px 10px',
                      fontSize: 11,
                      fontWeight: 700,
                      borderRadius: 4,
                      letterSpacing: '0.5px',
                    }}
                  >
                    HK$ {item.price}
                  </div>
                </div>

                {/* Product Body */}
                <div className="lp-class-body" style={{ padding: '20px 20px 24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div className="lp-class-tag" style={{ margin: 0 }}>
                      {item.gender ? (lang === 'zh' ? (item.gender === 'Female' ? '女款' : item.gender === 'Male' ? '男款' : '男女通用') : item.gender) : (lang === 'zh' ? '舞蹈用品' : 'Gear')}
                    </div>
                    {item.inStock !== false && (
                      <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
                        ● {lang === 'zh' ? '現貨發售' : 'In Stock'}
                      </span>
                    )}
                  </div>

                  <div className="lp-class-name" style={{ fontSize: 18, minHeight: 48, lineHeight: 1.3 }}>
                    {tField(item.name, lang)}
                  </div>

                  <p
                    className="lp-class-desc"
                    style={{
                      fontSize: 13,
                      color: '#666',
                      lineHeight: 1.5,
                      marginBottom: 16,
                      minHeight: 40,
                    }}
                  >
                    {tField(item.description, lang) || (lang === 'zh' ? '官方認證舞蹈裝備。' : 'Official ETS Academy product.')}
                  </p>

                  {/* Available Sizes */}
                  {Array.isArray(item.sizes) && item.sizes.length > 0 && (
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 6, letterSpacing: '1px' }}>
                        {lang === 'zh' ? '可選尺碼' : 'Available Sizes'}:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {item.sizes.map((sz, sIdx) => (
                          <span
                            key={sIdx}
                            style={{
                              background: '#f1f1f1',
                              color: '#333',
                              fontSize: 11,
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 4,
                              border: '1px solid #e0e0e0',
                            }}
                          >
                            {formatSize(sz, lang)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Order Button (ETS Wine Theme) */}
                  <button
                    onClick={() => handleWhatsAppInquiry(item)}
                    style={{
                      marginTop: 'auto',
                      width: '100%',
                      background: '#7b1d3a',
                      color: '#ffffff',
                      border: 'none',
                      padding: '12px 16px',
                      borderRadius: 6,
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      transition: 'background .25s ease, transform .15s ease',
                      boxShadow: '0 4px 12px rgba(123, 29, 58, 0.2)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#5d152c';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#7b1d3a';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <span>💬</span>
                    <span>{lang === 'zh' ? 'WhatsApp 諮詢訂購' : 'Order via WhatsApp'}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Swiper Controls */}
        <div className="lp-swiper-nav" style={{ marginTop: 32 }}>
          <button
            className="lp-swiper-btn"
            onClick={() => setSlideIdx((p) => Math.max(0, p - 1))}
            disabled={slideIdx === 0}
            style={{ opacity: slideIdx === 0 ? 0.4 : 1 }}
          >
            ←
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {Array.from({ length: maxIdx + 1 }).map((_, i) => (
              <div
                key={i}
                onClick={() => setSlideIdx(i)}
                style={{
                  width: i === slideIdx ? 24 : 8,
                  height: 8,
                  background: i === slideIdx ? '#7b1d3a' : '#e0e0e0',
                  cursor: 'pointer',
                  transition: 'all .3s',
                  borderRadius: 4,
                }}
              />
            ))}
          </div>
          <button
            className="lp-swiper-btn"
            onClick={() => setSlideIdx((p) => Math.min(maxIdx, p + 1))}
            disabled={slideIdx === maxIdx}
            style={{ opacity: slideIdx === maxIdx ? 0.4 : 1 }}
          >
            →
          </button>
        </div>
      </div>
    </section>
  );
}

